import { z } from 'zod';
import { uuid, isoTimestamp, syncColumns } from './common.js';
import { DATA_SOURCES } from '../enums.js';
/**
 * Row shapes for the sync set (DATABASE §12).
 *
 * `household_id` is deliberately absent from every schema here. API §1: it is
 * never a parameter — it is derived from the session and applied server-side.
 * A client that sends one is ignored, not trusted.
 */
export const listItemRow = syncColumns.extend({
    id: uuid,
    store_id: uuid.nullish(), // NULL = "any store"; sorts first (ARCHITECTURE §6.2)
    generic_product_id: uuid,
    variant_id: uuid.nullish(), // null = "any oat milk"
    quantity: z.number().int().positive(),
    note: z.string().max(500).nullish(),
    added_by_user: uuid,
    suggestion_reason: z.string().max(300).nullish(),
});
export const genericProductRow = syncColumns.extend({
    id: uuid,
    name: z.string().min(1).max(200),
    slug: z.string().min(1).max(200),
    category: z.string().max(100).nullish(),
    category_source: z.enum(DATA_SOURCES).default('auto'),
    /**
     * Whether this is an organic product (migration 019). Derived server-side
     * from the name (`shared/src/organic.ts`) and, like `slug`, **never read on
     * the way in** — a client cannot assert it, so it is optional on the row and
     * present on every pull.
     */
    organic: z.boolean().optional(),
});
export const productVariantRow = syncColumns.extend({
    id: uuid,
    generic_product_id: uuid,
    store_id: uuid,
    name: z.string().min(1).max(300),
    brand: z.string().max(200).nullish(),
    size_value: z.number().positive().nullish(),
    size_unit: z.string().max(20).nullish(),
    ean: z.string().max(20).nullish(),
    external_id: z.string().max(200).nullish(),
    /**
     * Where the picture came from, at the shop. Stored, never rendered — see
     * `image_path`.
     */
    image_url: z.string().max(1000).nullish(),
    /**
     * Where *this app* serves that picture from: `/api/v1/images/<hash>`,
     * same-origin (migration 015).
     *
     * Two fields rather than one rewritten field, because they are two different
     * facts and only one of them is safe to accept back. `image_url` is the
     * upstream truth and is what a future debugging session needs; `image_path`
     * is emitted by the server on the way out and **never read on the way in** —
     * `sync/apply.ts` ignores it. Rewriting `image_url` in place instead would
     * mean a client echoing a row back could overwrite the real source URL with
     * a proxy path, and the original would be gone.
     */
    image_path: z.string().max(200).nullish(),
    /**
     * The grey line under the generic — "Oatly Barista" beneath "Havermelk"
     * (migration 016). Written by the classification sweep, which already had
     * every fact the question needs, so it costs no extra model call.
     *
     * Server-emitted like `image_path`, and for the same reason: it is absent
     * from the sync apply function's upsert column list, so a phone echoing a
     * variant row back cannot blank a label it has no way to compute. Null until
     * the sweep reaches the row — `variantLabel()` in `shared/src/label.ts` is
     * what the screens show meanwhile, and on installs with no model at all.
     */
    short_name: z.string().max(64).nullish(),
    classification_source: z.enum(DATA_SOURCES).default('auto'),
    /**
     * Whether this is an organic product (migration 019). Derived server-side
     * from the store, the title and the brand (`shared/src/organic.ts`) — the
     * store-default half (Ekoplaza's whole range) needs the store's identity,
     * which a phone does not have, so like `slug` it is **never read on the way
     * in**: optional on the row, present on every pull.
     */
    organic: z.boolean().optional(),
    /**
     * Hidden from "Jullie producten" (migration 041). A plain LWW field, but
     * optional on the way in: a write that does not mention it (selection, an
     * older phone) leaves the stored value alone rather than un-hiding it.
     * Selection sends `false` explicitly — picking a product again un-hides it.
     */
    hidden: z.boolean().optional(),
    cross_store_product_id: uuid.nullish(),
    /**
     * The untouched upstream payload (ADAPTERS §2.1) — how a field rename at a
     * store is diagnosed without re-scraping, and what classification reads.
     *
     * Server-side only in practice: the API writes it at selection time and the
     * sync pull never returns it, so a phone neither sends nor receives one. It
     * lives on the row schema because there is one write path for this table and
     * it runs through the sync apply function.
     */
    raw: z.unknown().optional(),
});
export const variantAttributeRow = z.object({
    variant_id: uuid,
    key: z.string().min(1).max(100),
    value: z.string().min(1).max(200),
    source: z.enum(DATA_SOURCES).default('auto'),
});
export const suggestionDismissalRow = z.object({
    generic_product_id: uuid,
    store_id: uuid.nullish(),
    dismiss_count: z.number().int().nonnegative(),
    last_dismissed_at: isoTimestamp.nullish(),
    muted_until: isoTimestamp.nullish(),
});
export const storeCategoryOrderRow = z.object({
    store_id: uuid,
    category: z.string().min(1).max(100),
    position: z.number().int(),
    updated_at: isoTimestamp,
    updated_by_user: uuid.nullish(),
    updated_by_device: z.string().nullish(),
});
//# sourceMappingURL=rows.js.map